import { create } from "zustand";
import { LandingPages, Permission } from "../types/Authorization";
import Api from "../utils/Api";
import { jqXHR, ResponseError } from "../types/Api";

export const permissionsURL = (): string => {
  return "/ajax/permissions";
};

interface ResetOptions {
  supportAdmin?: boolean;
}

export interface SessionState {
  loaded: boolean;
  permissions: Permission[];
  supportAdmin: boolean;
  getOrFetch: () => Promise<Permission[]>;
  fetch: () => Promise<Permission[]>;
  getLandingPage: () => string;
  reset: (options?: ResetOptions) => void;
}

const synchronized: { fetchResult?: Promise<Permission[]> } = {};

/**
 * Provides a list of permissions granted to the current user in the selected device context.
 *
 * Usage example:
 * ```
 * export const MyPage: React.FC = () => {
 *   const [supportAdmin, permissions, permissionsLoaded] = useDevicePermissionsStore(
 *     useCallback((state) => [state.supportAdmin, state.permissions, state.loaded] as const, [])
 *   );
 *
 *   ...
 * };
 * ```
 */
export const useDevicePermissionsStore = create<SessionState>((set, get) => ({
  loaded: false,

  permissions: [],

  supportAdmin: false,

  /**
   * Gets permissions of the current user in the currently selected device context from the store,
   * or if the store is empty, then fetches the permissions from Account Management API first.
   *
   * NOTE: The user permissions are used in `AppRoot` to decide access to various routes,
   * conditionally render menu items and content on some pages. To avoid fetching the permissions
   * multiple times when UI loads, this method limits the number of in-flight requests to just 1
   * when invoked multiple times in quick succession and returns the same promise to all callers.
   *
   * @returns promise that completes with the current user device permissions.
   */
  getOrFetch: async (): Promise<Permission[]> => {
    const state = get();
    if (state.loaded) {
      return state.permissions;
    }

    if (synchronized.fetchResult) {
      return await synchronized.fetchResult;
    }

    synchronized.fetchResult = state.fetch();
    const permissions = await synchronized.fetchResult;
    synchronized.fetchResult = undefined;

    return permissions;
  },

  fetch: async (): Promise<Permission[]> => {
    let permissions,
      statusCode = 200;

    // eslint-disable-next-line
    await Api.getAsync(permissionsURL()).then(
      (response: { permissions: Permission[] }) => (permissions = response.permissions),
      (reason: jqXHR) => (statusCode = reason?.status)
    );

    if (!permissions || statusCode !== 200) {
      throw new ResponseError("Failed to fetch user permissions", statusCode);
    }

    set({ permissions, loaded: true });

    return permissions;
  },

  getLandingPage: (): string => {
    for (const page of LandingPages) {
      for (const userPermission of get().permissions) {
        if (page.permission === userPermission) {
          return page.page;
        }
      }
    }

    console.warn("Failed to determine landing page, defaulting to Statistics Dashboard");

    // should never make it here, but if we do, then try the most common default:
    // - best case the user does have access to the page
    // - worst case the user is displayed 'Access Denied' message
    // otherwise, no route would crash the routing and get user stuck
    return "/surfwize";
  },

  reset: ({ supportAdmin }: ResetOptions = {}) => {
    // under normal conditions, the `supportAdmin` property is not reset
    // as it does not change when switching between appliances
    if (typeof supportAdmin === "boolean") {
      set({
        loaded: false,
        permissions: [],
        supportAdmin,
      });
    } else {
      set({
        loaded: false,
        permissions: [],
      });
    }
  },
}));
